import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { EventsListSortField, EventsListState } from '@modules/calendar-app/types/events-list-state';
import { SortOrder } from '@modules/common/types/sort-order';
import { StitchTypeFilters } from '@modules/knowledge/types/stitch-type-filters';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { dateRangeChanges, setDateRangeSilently } from '@modules/common/utils/date-range-filter.util';
import { merge, Subject } from 'rxjs';
import { startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

const defaultSortOrder: Record<EventsListSortField, SortOrder> = {
  title: SortOrder.ASC,
  startTime: SortOrder.ASC,
  endTime: SortOrder.ASC,
  score: SortOrder.DESC,
};

@Component({
  selector: 'app-events-list-context-menu',
  templateUrl: './events-list-context-menu.component.html',
  styleUrls: ['./events-list-context-menu.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EventsListContextMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public state: EventsListState;
  @Input()
  public withAdvancedSearch: boolean;
  @Input()
  public withScore?: boolean;

  public innerFromDate: UntypedFormControl = new UntypedFormControl();
  public innerToDate: UntypedFormControl = new UntypedFormControl();
  public dateButtonSelected: 'from' | 'to' = 'from';
  public advancedSearchApplied: boolean;

  private readonly stateChanged = new Subject<void>();
  private readonly alive = new Subject<void>();
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly calendarAppStateService = inject(CalendarAppStateService);

  protected readonly virtualFolder = toSignal(this.calendarAppStateService.getVirtualFolder());

  public ngOnInit() {
    dateRangeChanges(this.innerFromDate, this.innerToDate)
      .pipe(takeUntil(this.alive))
      .subscribe(({ from, to }) => {
        if (from?.getTime() !== this.state.from?.getTime() || to?.getTime() !== this.state.to?.getTime()) {
          this.state.from = from;
          this.state.to = to;
          this.state.notifyStateChanged();
        }
      });

    merge(
      this.stateChanged.pipe(
        startWith(null),
        switchMap(() => this.state.changes().pipe(startWith(this.state))),
        tap(() => {
          this.innerFromDate.setValue(this.state.from, { emitEvent: false });
          this.innerToDate.setValue(this.state.to, { emitEvent: false });
        }),
      ),
      this.advancedSearchService.getApplied().pipe(
        tap((state) => {
          this.advancedSearchApplied = state;
        }),
      ),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('state' in changes) {
      this.stateChanged.next();
    }
  }

  public ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  public sort(sortBy: EventsListSortField) {
    let order = this.state.sortOrder;
    let by = this.state.sortBy;

    if (by === sortBy) {
      order = order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    } else {
      by = sortBy;
      order = defaultSortOrder[by] ?? SortOrder.ASC;
    }

    this.state.sortBy = by;
    this.state.sortOrder = order;
    this.state.notifyStateChanged();
  }

  public pinnedOnTop() {
    this.state.pinnedOnTop = !this.state.pinnedOnTop;
    this.state.notifyStateChanged();
  }

  public flaggedOnTop() {
    this.state.flaggedOnTop = !this.state.flaggedOnTop;
    this.state.notifyStateChanged();
  }

  public flag() {
    this.state.flagged = this.state.flagged ? null : true;
    this.state.notifyStateChanged();
  }

  public pin() {
    this.state.pinned = this.state.pinned ? null : true;
    this.state.notifyStateChanged();
  }

  public stitch(stitch: StitchTypeFilters) {
    this.state.stitchedWith = stitch;
    this.state.notifyStateChanged();
  }

  public today() {
    this.state.today = !this.state.today;
    this.state.notifyStateChanged();
  }

  public next7Days() {
    this.state.next7days = !this.state.next7days;
    this.state.notifyStateChanged();
  }

  /** Clears the picked date range — driven by the submenu "Clear" button, as the row's click toggles the popover. */
  public clearSelectedDate(): void {
    if (!this.state.from && !this.state.to) {
      return;
    }

    // Silent: the range is written to the state here in one go, instead of letting each control emit.
    setDateRangeSilently(this.innerFromDate, this.innerToDate, { from: null, to: null });
    this.state.from = null;
    this.state.to = null;
    this.state.notifyStateChanged();
  }
}
